import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Plus, Terminal, Trash2, TriangleAlert, X } from 'lucide-react';
import type { Site, SiteProvisioning } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatBytes, formatTimestamp, formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  ErrorNotice,
  Input,
  Pill,
  Spinner,
} from '../components/ui';

const CIDR = /^(\d{1,3}\.){3}\d{1,3}\/([0-9]|[12][0-9]|3[0-2])$/;

export function GatewayDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useSession();

  const [provisioning, setProvisioning] = useState<SiteProvisioning | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const siteQuery = useQuery({
    queryKey: ['site', id],
    queryFn: () => api.get<Site>(`/sites/${encodeURIComponent(id)}`),
    refetchInterval: 15_000,
  });

  const provision = useMutation({
    mutationFn: () => api.post<SiteProvisioning>(`/sites/${encodeURIComponent(id)}/provisioning`),
    onSuccess: setProvisioning,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/sites/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate('/gateways');
    },
  });

  if (siteQuery.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (siteQuery.error) return <ErrorNotice error={siteQuery.error} />;
  const site = siteQuery.data;
  if (!site) return null;

  return (
    <>
      <button
        onClick={() => navigate('/gateways')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to gateways
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-ink">{site.name}</h1>
            <Pill state
              tone={site.status === 'up' ? 'success' : site.status === 'down' ? 'danger' : 'warn'}
              dot
            >
              {site.status === 'pending' ? 'awaiting router' : site.status}
            </Pill>
            <Pill tone={site.tunnelType === 'wg' ? 'primary' : 'info'}>
              {site.tunnelType === 'wg' ? 'WireGuard' : 'OpenVPN'}
            </Pill>
          </div>
          <p className="mt-1 text-sm text-muted">
            Tunnel {site.tunnelIp ?? '—'} · ACS side {site.acsTunnelIp}
            {site.publicEndpoint ? ` · router at ${site.publicEndpoint}` : ''}
          </p>
        </div>

        {can('site.manage') && (
          <div className="flex flex-wrap gap-2">
            <Button
              icon={<Terminal className="size-4" />}
              loading={provision.isPending}
              onClick={() => provision.mutate()}
            >
              Router script
            </Button>
            <Button
              variant="danger"
              icon={<Trash2 className="size-4" />}
              onClick={() => {
                if (confirm(`Delete ${site.name}? The tunnel drops and its ONUs become unreachable.`)) {
                  remove.mutate();
                }
              }}
            >
              Delete
            </Button>
          </div>
        )}
      </div>

      {notice && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[2fr_3fr]">
        <Card title="Tunnel status">
          <dl className="divide-y divide-line">
            {[
              ['Status', site.status === 'pending' ? 'Awaiting router' : site.status],
              ['Tunnel address', site.tunnelIp ?? '—'],
              ['ACS address', site.acsTunnelIp],
              // Platform only (2026-09-18): a tenant is not told their gateway
              // translates, nor onto what.
              ...(can('org.manage')
                ? [
                    [
                      'Address translation',
                      site.addressTranslation === 'netmap'
                        ? 'On — ranges mapped 1:1 onto platform blocks'
                        : 'Off — ranges routed as they are',
                    ],
                  ]
                : []),
              ['Router endpoint', site.publicEndpoint ?? '—'],
              ['Last handshake', formatRelative(site.lastHandshakeAt)],
              ['Received', formatBytes(site.rxBytes)],
              ['Sent', formatBytes(site.txBytes)],
              ['ONUs behind it', String(site.deviceCount ?? 0)],
              ['Created', formatTimestamp(site.createdAt)],
            ].map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-4 px-5 py-2.5">
                <dt className="text-sm text-muted">{label}</dt>
                <dd className="text-sm text-ink">{value}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <SubnetsCard site={site} onChanged={setNotice} />
      </div>

      {provisioning && (
        <ProvisioningCard provisioning={provisioning} onClose={() => setProvisioning(null)} />
      )}
    </>
  );
}

/**
 * The routed ranges behind this gateway. Saving re-applies the peer's AllowedIPs
 * on the ACS immediately, which is what makes the ACS route to those networks —
 * the router still needs its script re-run, since the ranges also drive its
 * address list.
 */
interface SubnetRow {
  cidr: string;
  label: string;
  /** The platform block the tunnel routes for this range — shown, never edited. */
  mapped: string;
}

/** Common purposes, offered as suggestions so labels stay consistent. */
const LABEL_SUGGESTIONS = [
  'ONU management',
  'OLT management',
  'Customer LAN',
  'CPE management',
  'Voice',
];

function SubnetsCard({ site, onChanged }: { site: Site; onChanged: (message: string) => void }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  /**
   * Address translation is the platform's setting and the platform's
   * information (2026-09-18): the switch, the "On the tunnel" column and the
   * explanation render for a platform reader only. A tenant's gateway is
   * translated by default and their page simply lists their ranges.
   */
  const platform = can('org.manage');

  const asRows = (subnets: Site['managedSubnets']): SubnetRow[] =>
    subnets.map((subnet) => ({ cidr: subnet.cidr, label: subnet.label ?? '', mapped: subnet.mapped ?? '' }));

  const [rows, setRows] = useState<SubnetRow[]>(asRows(site.managedSubnets));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setRows(asRows(site.managedSubnets)), [site.managedSubnets]);

  const dirty = JSON.stringify(rows) !== JSON.stringify(asRows(site.managedSubnets));
  const invalid = rows.filter((row) => row.cidr.trim() && !CIDR.test(row.cidr.trim()));

  /**
   * The switch. On issues every range a platform block and re-applies the
   * tunnel routing; off drops the blocks and routes the ranges themselves.
   * Either way the router's script changes, so the notice says to re-run it.
   */
  const translate = useMutation({
    // acknowledgeCutover: the confirm below is where the operator read it.
    mutationFn: (addressTranslation: Site['addressTranslation']) =>
      api.patch<Site>(`/sites/${encodeURIComponent(site.id)}`, { addressTranslation, acknowledgeCutover: true }),
    onSuccess: async (_, addressTranslation) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      onChanged(
        addressTranslation === 'netmap'
          ? 'Address translation is on: every range was issued a platform block and the ACS now routes those. Re-run the router script on this gateway — it carries the netmap rules — or nothing behind it is reachable until you do.'
          : 'Address translation is off: the ACS now routes the ranges themselves. Re-run the router script on this gateway so its netmap rules are removed.',
      );
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not change address translation'),
  });

  /**
   * WHERE the translation is done (2026-09-19). On the server the gateway
   * gets its own WireGuard interface and port on the ACS host, the kernel
   * maps the ranges there, and the router script carries no NAT rule; on the
   * router the script carries the netmap rules. Moving it re-homes the peer
   * the moment it is saved, so the router has to run the new script at once.
   */
  const relocate = useMutation({
    mutationFn: (translationAt: 'router' | 'server') =>
      api.patch<Site>(`/sites/${encodeURIComponent(site.id)}`, { translationAt, acknowledgeCutover: true }),
    onSuccess: async (_, translationAt) => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      onChanged(
        translationAt === 'server'
          ? 'The translation now runs on the server, on this gateway\'s own interface. Re-run the router script on this gateway — it dials a new port and carries no NAT rule — or nothing behind it is reachable until you do.'
          : 'The translation is back on the router. Re-run the router script on this gateway — it dials the shared port again and carries the netmap rules.',
      );
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not move the translation'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch<Site>(`/sites/${encodeURIComponent(site.id)}`, {
        managedSubnets: rows
          .filter((row) => row.cidr.trim())
          .map((row) => ({
            cidr: row.cidr.trim(),
            ...(row.label.trim() ? { label: row.label.trim() } : {}),
          })),
      }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
      onChanged(
        'Routing updated on the ACS. Re-run the router script on this gateway so its address list matches — until then the router will not pass traffic for a newly added range.',
      );
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save'),
  });

  return (
    <Card
      title="Connected subnets"
      action={
        can('site.manage') ? (
          <Button
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => setRows([...rows, { cidr: '', label: '', mapped: '' }])}
          >
            Add subnet
          </Button>
        ) : null
      }
    >
      <div className="space-y-3 p-5">
        <p className="text-sm text-muted">
          Networks reachable through this tunnel — ONU management ranges, and the OLT management
          subnet when the terminal sits behind this router. The ACS routes to exactly these.
        </p>

        {platform && (
          <label className="flex items-start gap-2 rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={site.addressTranslation === 'netmap'}
              disabled={translate.isPending}
              onChange={(e) => {
                const next = e.target.checked ? 'netmap' : 'none';
                const onus = site.deviceCount ?? 0;
                // The tunnel changes the moment this is saved; the router only
                // when its new script runs. Everything behind the gateway is
                // off the platform in between (2026-09-18).
                const ok = window.confirm(
                  `Switching address translation ${next === 'netmap' ? 'on' : 'off'} cuts everything behind this gateway off from the platform until the router has run the new script${onus ? ` — ${onus} ONU${onus === 1 ? '' : 's'} inform through it` : ''}. Fetch and run the router script straight after. Continue?`,
                );
                if (ok) translate.mutate(next);
              }}
            />
            <span>
              Translate addresses on the tunnel
              <span className="block text-xs text-muted">
                {site.addressTranslation === 'netmap'
                  ? 'Each range is mapped one-to-one onto the platform block shown beside it. The platform dials OLTs and ONUs at the mapped address; the tenant\'s own addressing stays theirs, and another organization may use the same ranges.'
                  : 'The ranges themselves are routed on the tunnel, so they must be free across the whole platform. Switch on to map them onto platform blocks and re-run the router script.'}
              </span>
            </span>
          </label>
        )}

        {platform && site.addressTranslation === 'netmap' && site.tunnelType === 'wg' && (
          <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm">
            <span className="font-medium text-ink">Translated on</span>
            <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-2">
              {(
                [
                  ['server', 'the server', 'This gateway has its own interface on the ACS host; the kernel maps the ranges there. The router runs no NAT rule for the platform.'],
                  ['router', 'the router', 'The router script carries the netmap rules and does the mapping itself.'],
                ] as const
              ).map(([at, label, hint]) => (
                <label key={at} className="flex items-start gap-2" title={hint}>
                  <input
                    type="radio"
                    name="translation-at"
                    className="mt-0.5"
                    checked={(site.translationAt ?? 'server') === at}
                    disabled={relocate.isPending}
                    onChange={() => {
                      const onus = site.deviceCount ?? 0;
                      const ok = window.confirm(
                        `Moving the translation to ${label} re-homes this gateway's tunnel on the server the moment it is saved; the router keeps dialling the old interface until it has run the new script, and everything behind the gateway is off the platform in between${onus ? ` — ${onus} ONU${onus === 1 ? '' : 's'} inform through it` : ''}. Fetch and run the router script straight after. Continue?`,
                      );
                      if (ok) relocate.mutate(at);
                    }}
                  />
                  <span>
                    {label}
                    <span className="block text-xs text-muted">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {site.translationAt === 'server' && site.serverListenPort ? (
              <p className="mt-2 text-xs text-muted">
                The router dials UDP port <span className="font-mono text-ink">{site.serverListenPort}</span> on the
                server for this gateway — open it on the server's firewall if one sits in front of it.
              </p>
            ) : null}
          </div>
        )}

        {rows.length === 0 && (
          <p className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-sm text-faint">
            No subnets. The ACS cannot reach anything behind this gateway.
          </p>
        )}

        {rows.length > 0 && (
          <div className={`grid ${platform ? 'grid-cols-[1fr_1.2fr_1fr_auto]' : 'grid-cols-[1fr_1.2fr_auto]'} gap-2 px-1`}>
            <span className="text-xs font-medium text-muted">Name</span>
            <span className="text-xs font-medium text-muted">Subnet</span>
            {platform && <span className="text-xs font-medium text-muted">On the tunnel</span>}
            <span className="w-9" />
          </div>
        )}

        <div className="space-y-2">
          {rows.map((row, index) => {
            const badCidr = Boolean(row.cidr.trim()) && !CIDR.test(row.cidr.trim());
            const update = (patch: Partial<SubnetRow>) =>
              setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

            return (
              <div
                key={index}
                className={`grid ${platform ? 'grid-cols-[1fr_1.2fr_1fr_auto]' : 'grid-cols-[1fr_1.2fr_auto]'} gap-2`}
              >
                <Input
                  value={row.label}
                  onChange={(e) => update({ label: e.target.value })}
                  placeholder="ONU management"
                  list="subnet-label-suggestions"
                  maxLength={60}
                  disabled={!can('site.manage')}
                />
                <Input
                  value={row.cidr}
                  onChange={(e) => update({ cidr: e.target.value })}
                  placeholder="10.10.0.0/16"
                  className={
                    badCidr
                      ? 'font-mono border-danger focus:border-danger focus:ring-danger'
                      : 'font-mono'
                  }
                  disabled={!can('site.manage')}
                />
                {platform && (
                  <span
                    className="self-center truncate font-mono text-xs text-muted"
                    title={row.mapped ? 'The platform block the tunnel routes for this range' : undefined}
                  >
                    {row.mapped
                      ? row.mapped
                      : site.addressTranslation === 'netmap'
                        ? row.cidr.trim()
                          ? 'issued on save'
                          : ''
                        : 'as is'}
                  </span>
                )}
                {can('site.manage') ? (
                  <Button
                    variant="ghost"
                    icon={<X className="size-4" />}
                    onClick={() => setRows(rows.filter((_, i) => i !== index))}
                    title="Remove"
                  />
                ) : (
                  <span className="w-9" />
                )}
              </div>
            );
          })}
        </div>

        <datalist id="subnet-label-suggestions">
          {LABEL_SUGGESTIONS.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>

        {invalid.length > 0 && (
          <p className="text-xs text-danger">
            {invalid.length} entr{invalid.length === 1 ? 'y is' : 'ies are'} not a valid IPv4 CIDR.
          </p>
        )}
        {error && <ErrorNotice error={new Error(error)} />}

        {can('site.manage') && (
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="primary"
              disabled={!dirty || invalid.length > 0}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save routing
            </Button>
            {dirty && (
              <Button onClick={() => setRows(asRows(site.managedSubnets))}>Discard</Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function ProvisioningCard({
  provisioning,
  onClose,
}: {
  provisioning: SiteProvisioning;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (label: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1200);
  };

  return (
    <Card
      className="mt-5"
      title="Router provisioning"
      action={
        <Button size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4 p-5">
        <div>
          <span className="label">Run this on the router</span>
          <div className="flex gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs text-ink">
              {provisioning.fetchCommand}
            </code>
            <Button
              size="sm"
              icon={<Copy className="size-3.5" />}
              onClick={() => copy('cmd', provisioning.fetchCommand)}
            >
              {copied === 'cmd' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="label mb-0">Or paste the script directly</span>
            <Button
              size="sm"
              icon={<Copy className="size-3.5" />}
              onClick={() => copy('script', provisioning.script)}
            >
              {copied === 'script' ? 'Copied' : 'Copy script'}
            </Button>
          </div>
          <pre className="max-h-96 overflow-auto rounded-lg border border-line bg-surface-2 p-4 font-mono text-[11px] leading-relaxed text-muted">
            {provisioning.script}
          </pre>
        </div>
      </div>
    </Card>
  );
}

export function GatewayLink({ site }: { site: Site }) {
  return (
    <Link to={`/gateways/${site.number ?? site.id}`} className="text-link hover:underline">
      {site.name}
    </Link>
  );
}
